// src/app/_service/space-context.service.ts
// The "currently selected Space for new notes" (tasks/space_core_client.md, Stage 2 §1). This is
// UI state, not app data: per-device (localStorage, like the JWT itself), never synced, never
// stored in IndexedDB's `notes` store, and resets to the user's PRIVATE Space on a fresh device or
// browser profile. Switching it never affects any existing note — only notes created after the
// switch pick it up (see DataOfflineService.createNote()/createNoteFromTemplate()/createNoteCopy()).

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';

import { SpaceService } from './space.service';
import { AuthService } from './auth.service';
import { targetSuffix } from '../_util/data-target';

const ACTIVE_SPACE_KEY_PREFIX = 'notes:active_space';

@Injectable({ providedIn: 'root' })
export class SpaceContextService {
  private readonly activeSpaceIdSubject = new BehaviorSubject<string | null>(null);
  readonly activeSpaceId$: Observable<string | null> = this.activeSpaceIdSubject.asObservable();

  constructor(
    private readonly spaceService: SpaceService,
    private readonly auth: AuthService,
  ) {}

  private get storageKey(): string {
    return `${ACTIVE_SPACE_KEY_PREFIX}${targetSuffix()}::${this.auth.getUsername() ?? 'anonymous'}`;
  }

  getActiveSpaceId(): string | null {
    return this.activeSpaceIdSubject.value;
  }

  /**
   * Loads the persisted active-Space choice for this device/account, called once at app startup
   * and again after every login (a different account can have different Spaces). Defaults to (and
   * persists) the user's own PRIVATE Space when there is no stored choice yet, or the stored
   * choice no longer matches a Space the user actually belongs to (e.g. removed from a SHARED
   * Space on another device since the last time this device loaded).
   *
   * Never blocks/throws on a network failure (offline-first): if Spaces can't be listed right now,
   * whatever was already selected (from localStorage, or the in-memory default) is left as-is —
   * new notes created before this resolves simply omit spaceId, which the server already treats as
   * "the caller's own PRIVATE Space" (see tasks/space_core_client.md Stage 1), so nothing breaks.
   */
  async initialize(): Promise<void> {
    const stored = localStorage.getItem(this.storageKey);

    let spaces;
    try {
      spaces = await firstValueFrom(this.spaceService.getSpaces());
    } catch {
      if (stored) {
        this.activeSpaceIdSubject.next(stored);
      }
      return;
    }

    if (stored && spaces.some((space) => space.id === stored)) {
      this.activeSpaceIdSubject.next(stored);
      return;
    }

    const privateSpaceId = spaces.find((space) => space.type === 'PRIVATE')?.id ?? null;
    this.activeSpaceIdSubject.next(privateSpaceId);
    if (privateSpaceId) {
      localStorage.setItem(this.storageKey, privateSpaceId);
    }
  }

  /** Switches the active Space for notes created from now on. Does not touch any existing note. */
  setActiveSpaceId(spaceId: string): void {
    this.activeSpaceIdSubject.next(spaceId);
    localStorage.setItem(this.storageKey, spaceId);
  }
}
